// Shared utility for network tags
export const TAG_CATEGORIES = {
  FREQUENCY: 'frequency',
  OTHER: 'other'
};

export const TAG_DEFINITIONS = {
  // frequency tags
  'meets-weekly': { category: TAG_CATEGORIES.FREQUENCY, color: '#0088CC' },
  'meets-monthly': { category: TAG_CATEGORIES.FREQUENCY, color: '#4197C3' },
  'meets-regularly': { category: TAG_CATEGORIES.FREQUENCY, color: '#296C8E' },

  // other tags
  'webinar': { category: TAG_CATEGORIES.OTHER, color: '#229ED6' },
  'case-discussion': { category: TAG_CATEGORIES.OTHER, color: '#1492CB' },
  'research': { category: TAG_CATEGORIES.OTHER, color: '#3F94BB' },
  'peer-training': { category: TAG_CATEGORIES.OTHER, color: '#006A9B' },
  'resource-library': { category: TAG_CATEGORIES.OTHER, color: '#3E7994' },
  'tumor-board': { category: TAG_CATEGORIES.OTHER, color: '#0B7A8C' },
};

export const DEFAULT_TAG_COLOR = '#6c757d'; // gray

/**
 * Get the color for a specific tag ID
 * @param {string} tagId 
 * @returns {string} hex color
 */
export function getTagColor(tagId) {
  return TAG_DEFINITIONS[tagId]?.color || DEFAULT_TAG_COLOR;
}

/**
 * Get the category for a specific tag ID
 * @param {string} tagId 
 * @returns {string} category
 */
export function getTagCategory(tagId) {
  return TAG_DEFINITIONS[tagId]?.category || null;
}

/**
 * Get all tag IDs for a specific category
 * @param {string} category 
 * @returns {string[]} array of tag IDs
 */
export function getTagIdsByCategory(category) {
  return Object.keys(TAG_DEFINITIONS).filter(
    tagId => TAG_DEFINITIONS[tagId].category === category
  );
}

/**
 * Create a tag object with id, label, and selected state
 * @param {string} tagId 
 * @param {string} label - Already translated label
 * @param {boolean} selected 
 * @returns {Object}
 */
export function createTagObject(tagId, label, selected = false) {
  return {
    id: tagId,
    label: label,
    selected: selected,
    category: getTagCategory(tagId),
    color: getTagColor(tagId)
  };
}

/**
 * Sort an array of tag IDs by their declaration order in TAG_DEFINITIONS
 * @param {string[]} tagIds - Array of tag IDs to sort
 * @returns {string[]} - Sorted array of tag IDs
 */
export function sortTagsByDeclarationOrder(tagIds) {
  const declarationOrder = Object.keys(TAG_DEFINITIONS);
  return tagIds.sort((a, b) => {
    const indexA = declarationOrder.indexOf(a);
    const indexB = declarationOrder.indexOf(b);
    // if tag is not found in definitions, put it at the end
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

/**
 * Format tag name with intelligent handling of medical abbreviations
 * Preserves all-caps words and alphanumeric codes while applying PascalCase to regular words
 * Uses hyphens only immediately after acronyms or alphanumeric codes
 * @param {string} name - The tag name to format
 * @returns {string} - Formatted tag name
 */
export function formatTag(name) {
  if (!name || typeof name !== 'string') {
    return '';
  }

  const words = name.trim().split(/\s+/);
  const result = [];

  for (let i = 0; i < words.length; i++) {
    const word = words[i];

    // check if word is all caps with 2+ letters (medical abbreviation)
    if (/^[A-Z]{2,}$/.test(word)) {
      result.push(word); // preserve as-is: ALL, CNS, MRI, ICU
      // add hyphen if there are more words after this acronym
      if (i < words.length - 1) {
        result.push('-');
      }
    }
    // check if word contains numbers or hyphens (alphanumeric codes)
    else if (/[\d-]/.test(word) && /[A-Z]/.test(word)) {
      result.push(word); // preserve as-is: CCG-1922, DFCI-ALL
      // add hyphen if there are more words after this code
      if (i < words.length - 1) {
        result.push('-');
      }
    }
    // regular word, apply PascalCase and concatenate with previous if it was also regular
    else {
      const formattedWord = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      result.push(formattedWord);
    }
  }

  return result.join('');
}